import React, { useCallback, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import { useParams } from "react-router";

import { useShopping } from "../../../contexts/shopping";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import { ADD_SHOPPING_INVENTORY_ITEM_MUTATION } from "../../../constants/graphql/index";
import { addInventoryFields } from "../Cart/CartItemFields";
import AddToCartAction from "./AddToCartAction";
import QuantitySelector from "./QuantitySelector";

const AddToCart = ({ asset, defaultValues, size = "medium", onSuccess }) => {
  const { projectId } = useParams();
  const { notifySuccess } = useNotification();
  const {
    inventoryItems,
    setInventoryAsset,
    setInventoryItem,
    removeInventoryItem,
    refetch,
  } = useShopping();
  const inventoryItem = useMemo(
    () => Object.values(inventoryItems || {}).find(item => item.assetId === asset.id),
    [asset.id, inventoryItems]
  );
  const notAvailable = asset.availableQuantity <= 0;
  const [quantity, setQuantity] = useState(notAvailable ? 0 : 1);
  const [error, setError] = useState(null);
  const [addInventoryItem, { loading }] = useMutation(ADD_SHOPPING_INVENTORY_ITEM_MUTATION);

  const updateQuantity = useCallback(
    value => {
      if (value >= 0 && value <= asset.availableQuantity) {
        setQuantity(value);
      }
    },
    [asset.availableQuantity, setQuantity]
  );

  const handleMinus = useCallback(() => updateQuantity(quantity - 1), [quantity, updateQuantity]);

  const handlePlus = useCallback(() => updateQuantity(quantity + 1), [quantity, updateQuantity]);

  const handleUpdate = useCallback(
    event => updateQuantity(event.target.value ? parseInt(event.target.value, 10) : 0),
    [quantity, updateQuantity]
  );

  const handleAddInventoryItem = () => {
    if (quantity === 0 && !inventoryItem) return;

    const variables = addInventoryFields({
      ...inventoryItem,
      quantity,
      projectId,
      assetId: asset.id,
      ...defaultValues,
    });

    return addInventoryItem({
      variables,
      onSuccess: ({ inventoryItem: updatedInventoryItem, asset: updatedAsset }) => {
        setError(null);
        setQuantity(updatedAsset.availableQuantity > 0 ? 1 : 0);
        if (updatedInventoryItem && updatedAsset) {
          setInventoryAsset(updatedAsset);
        }
        updatedInventoryItem ? setInventoryItem(updatedInventoryItem) : removeInventoryItem(inventoryItem?.id);
        notifySuccess("Asset successfully added", {
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "right",
          },
        });
        refetch();
        onSuccess?.(updatedAsset);
      },
      onFailure: errors => {
        setError(errors.quantity || errors.asset || errors.submit);
      },
    });
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" width="100%" gap={1}>
        <QuantitySelector
          quantity={quantity}
          onMinus={handleMinus}
          onPlus={handlePlus}
          onChange={handleUpdate}
          disabled={notAvailable || loading}
        />
        <AddToCartAction
          asset={asset}
          notAvailable={notAvailable}
          addInventoryItem={handleAddInventoryItem}
          defaultValues={defaultValues}
        />
      </Box>
      {error && (
        <Box mt={1}>
          <FormHelperText htmlFor="barcode" error={!!error}>
            {error}
          </FormHelperText>
        </Box>
      )}
    </>
  );
};

export default AddToCart;
